import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import { Badge, Button, Card, EmptyState, ErrorNote, Field, Input, Modal, PageHeader, Select, Spinner, statusTone, Table } from "../components/ui";
import { fmtDateTime } from "../lib/format";

type Alert = {
  id: string; name: string; condition: { metric: string; operator: string; threshold: number; scope?: string };
  channels: string[]; enabled: boolean; lastTriggeredAt: string | null; createdAt: string;
};

const METRICS: Record<string, string> = {
  inbox_pct: "Inbox placement %",
  spam_pct: "Spam placement %",
  missing_pct: "Missing %",
  category_pct: "Category/Promotions %",
  deliverability_score: "Deliverability score",
  provider_inbox_pct: "Provider inbox %",
  auth_spf: "SPF",
  auth_dkim: "DKIM",
  auth_dmarc: "DMARC",
  blacklist_domain: "Domain blacklisted",
  blacklist_ip: "IP blacklisted",
};

const EMPTY = { name: "", metric: "inbox_pct", operator: "lt", threshold: 80, scope: "", channels: ["in_app"] as string[], enabled: true };

export function AlertsPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const { data: alerts } = useQuery({ queryKey: ["alerts"], queryFn: () => api<Alert[]>("/alerts") });
  const { data: events } = useQuery({ queryKey: ["alert-events"], queryFn: () => api<{ items: any[] }>("/alerts/events?pageSize=10") });
  const save = useMutation({
    mutationFn: (payload: any) =>
      editId ? api(`/alerts/${editId}`, { method: "PATCH", body: JSON.stringify(payload) }) : api("/alerts", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts"] }); setModal(false); setForm(EMPTY); setEditId(null); },
    onError: setError,
  });
  const del = useMutation({ mutationFn: (id: string) => api(`/alerts/${id}`, { method: "DELETE", body: "{}" }), onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }) });

  return (
    <div>
      <PageHeader title="Alerts" subtitle="Get notified when deliverability metrics cross thresholds" actions={<Button onClick={() => { setForm(EMPTY); setEditId(null); setError(null); setModal(true); }}>+ New Alert</Button>} />
      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <Card className="p-0">
          {!alerts?.length ? (
            <div className="p-6"><EmptyState title="No alerts configured" description="Add alerts for inbox %, spam %, authentication failures, or blacklist status." /></div>
          ) : (
            <Table headers={["Name", "Condition", "Channels", "Status", "Last Triggered", "Actions"]}>
              {alerts!.map((a) => (
                <tr key={a.id} className="table-row-hover">
                  <td className="px-4 py-3 font-medium text-ink-800">{a.name}</td>
                  <td className="px-4 py-3 text-xs text-ink-600">
                    {METRICS[a.condition.metric] ?? a.condition.metric} {a.condition.operator} {a.condition.threshold}
                    {a.condition.scope ? ` (${a.condition.scope})` : ""}
                  </td>
                  <td className="px-4 py-3"><div className="flex gap-1">{a.channels.map((c) => <Badge key={c} tone="brand">{c.replace("_", " ")}</Badge>)}</div></td>
                  <td className="px-4 py-3">{a.enabled ? <Badge tone="success">Enabled</Badge> : <Badge tone="neutral">Disabled</Badge>}</td>
                  <td className="px-4 py-3 text-xs text-ink-500">{fmtDateTime(a.lastTriggeredAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button className="rounded px-2 py-1 text-xs text-brand-600 hover:bg-brand-50" onClick={() => { setForm({ ...EMPTY, ...a, scope: a.condition.scope ?? "" }); setEditId(a.id); setModal(true); }}>Edit</button>
                      <button className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => { if (confirm(`Delete alert ${a.name}?`)) del.mutate(a.id); }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
        <Card title="Recent alert events" subtitle="Latest triggered notifications">
          {!events?.items.length ? (
            <p className="py-6 text-center text-sm text-ink-400">No events yet.</p>
          ) : (
            <div className="space-y-3">
              {events!.items.map((e) => (
                <div key={e.id} className="rounded-lg border border-ink-100 p-3">
                  <div className="flex items-center justify-between">
                    <Badge tone={e.severity === "critical" ? "danger" : "warning"}>{e.severity}</Badge>
                    <span className="text-[11px] text-ink-400">{fmtDateTime(e.createdAt)}</span>
                  </div>
                  <p className="mt-2 text-xs text-ink-600">{e.message}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Edit Alert" : "New Alert"}>
        <div className="space-y-4">
          <ErrorNote error={error} />
          <Field label="Alert name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Inbox below 80%" /></Field>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Metric">
              <Select value={form.metric} onChange={(e) => setForm({ ...form, metric: e.target.value })}>
                {Object.entries(METRICS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </Field>
            <Field label="Operator">
              <Select value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })}>
                <option value="lt">below (&lt;)</option>
                <option value="gt">above (&gt;)</option>
                <option value="lte">at or below</option>
                <option value="gte">at or above</option>
                <option value="eq">equals</option>
              </Select>
            </Field>
            <Field label="Threshold"><Input type="number" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: Number(e.target.value) })} /></Field>
          </div>
          {form.metric === "provider_inbox_pct" && (
            <Field label="Provider scope">
              <Select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                <option value="gmail">Gmail</option>
                <option value="google_workspace">Google Workspace</option>
                <option value="microsoft_365">Microsoft 365</option>
                <option value="outlook">Outlook</option>
                <option value="yahoo">Yahoo</option>
              </Select>
            </Field>
          )}
          <Field label="Channels">
            <div className="flex gap-4">
              {["in_app", "email"].map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.channels.includes(c)} onChange={(e) => setForm({ ...form, channels: e.target.checked ? [...form.channels, c] : form.channels.filter((x) => x !== c) })} className="h-4 w-4 rounded text-brand-600" />
                  {c === "in_app" ? "In-app" : "Email"}
                </label>
              ))}
            </div>
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button>
            <Button onClick={() => save.mutate({ name: form.name, condition: { metric: form.metric, operator: form.operator, threshold: form.threshold, scope: form.scope || undefined }, channels: form.channels, enabled: form.enabled })} disabled={save.isPending}>Save alert</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
